import React, { useEffect, useRef, useState, useMemo } from "react";
import dayjs from "dayjs";
import html2canvas from 'html2canvas';
import TailwindBarChart from '../Tailwind/TailwindBarChart';
import { useChartPalette } from "../Tailwind/chartColors";

export default function BarChartItem({ name, x_axis, data, graduation_one_step_y = false, x_axis_string = false }) {
    const [clipBoardLoading, setClipBoardLoading] = useState(false);
    const graphRef = useRef(null);

    useEffect(() => {
        if (clipBoardLoading && graphRef.current) {
            html2canvas(graphRef.current, {
                onclone: (clonedDoc, clonedEl) => {
                    const btn = clonedEl.querySelector('[data-copy-btn]');
                    if (btn) btn.remove();
                    const title = clonedEl.querySelector('h3');
                    if (title) title.style.paddingBottom = '4px';
                },
            }).then(canvas => {
                canvas.toBlob(blob => {
                    const item = new ClipboardItem({ 'image/png': blob });
                    navigator.clipboard.write([item]);
                    setClipBoardLoading(false);
                });
            });
        }
    }, [clipBoardLoading]);

    const handleCopy = () => {
        window.focus();
        setClipBoardLoading(true);
    };

    const palette = useChartPalette();

    const seriesKeys = useMemo(() =>
        Object.keys(data).filter(k => k !== x_axis.key),
        [data, x_axis.key]
    );

    // Transform column-based data to recharts row-based format
    const chartData = useMemo(() => {
        const xData = data[x_axis.key].data;
        return xData.map((xValue, i) => {
            const row: any = { x: x_axis_string ? xValue : dayjs(xValue).year().toString() };
            for (const key of seriesKeys) {
                row[key] = data[key].data[i];
            }
            return row;
        });
    }, [data, x_axis.key, seriesKeys, x_axis_string]);

    const series = useMemo(() =>
        seriesKeys.map((seriesName, index) => ({
            dataKey: seriesName,
            label: seriesName,
            color: palette[index % palette.length],
        })),
        [seriesKeys, palette]
    );

    const chartHeight = useMemo(() => Math.max(300, 300 + (seriesKeys.length - 5) * 20), [seriesKeys.length]);

    return (
        <div className="min-w-0">
            <div ref={graphRef} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm overflow-hidden min-w-0">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900 truncate">{name}</h3>
                    <button
                        data-copy-btn
                        onClick={handleCopy}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors group relative flex-shrink-0"
                        title="Copy to clipboard"
                    >
                        {!clipBoardLoading ? (
                            <span className="material-symbols-outlined text-[20px] text-slate-500">content_copy</span>
                        ) : (
                            <div className="w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                        )}
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Copy to clipboard
                        </span>
                    </button>
                </div>
                <div className="min-w-0 w-full">
                    <TailwindBarChart
                        data={chartData}
                        series={series}
                        xAxisKey="x"
                        height={chartHeight}
                        showGrid={true}
                        showLegend={true}
                        showTooltip={true}
                    />
                </div>
            </div>
        </div>
    );
}
